import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await authApi.requestPasswordReset(email);
      setDone(true);
    } catch (err) {
      // Older API versions return 404 for unknown emails; show the same
      // confirmation so the page never reveals which emails exist (OWASP A07).
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) {
        setDone(true);
        return;
      }
      setError(t("We couldn't send a password reset email. Please try again in a moment."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>{t('Reset your password')}</CardTitle>
          <CardDescription>
            {t('We will email you a link to choose a new password.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <p className="text-sm">
              {t("If an account exists for that email, we've sent a password reset link.")}
            </p>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="email">{t('Email')}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
              <Button type="submit" className="w-full" disabled={submitting}>
                {submitting ? t('Sending…') : t('Send reset link')}
              </Button>
              <p className="text-center text-xs">
                <Link to="/login" className="text-primary hover:underline">
                  {t('Back to sign in')}
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
